import { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Save, X, Image as ImageIcon, Upload, List } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import MediaUpload from '../../components/MediaUpload';
import Papa from 'papaparse';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MenuManagement = () => {
  const [menus, setMenus] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [menuToDelete, setMenuToDelete] = useState(null);

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');

  const [form, setForm] = useState({
    name: '',
    slug: '',
    category: 'Makanan',
    price: '',
    description: '',
    image: '',
    isRecommended: false,
    is_active: true
  });

  const fileInputRef = useRef(null);

  const getToken = () => localStorage.getItem('token');

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/menus/categories`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Gagal mengambil kategori', error);
    }
  };

  const fetchMenus = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/menus`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setMenus(response.data);
    } catch (error) {
      toast.error('Gagal mengambil data menu');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchMenus();
  }, []);

  const openCreate = () => {
    setEditingMenu(null);
    setForm({
      name: '',
      slug: '',
      category: categories.length > 0 ? categories[0].name : '',
      price: '',
      description: '',
      image: '',
      isRecommended: false,
      is_active: true
    });
    setShowModal(true);
  };

  const openEdit = (menu) => {
    setEditingMenu(menu);
    setForm({
      name: menu.name || '',
      slug: menu.slug || '',
      category: menu.category || (categories.length > 0 ? categories[0].name : ''),
      price: menu.price || '',
      description: menu.description || '',
      image: menu.image || '',
      isRecommended: menu.isRecommended || false,
      is_active: menu.is_active !== false // default true
    });
    setShowModal(true);
  };

  const confirmDelete = (menu) => {
    setMenuToDelete(menu);
    setShowDeleteModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.category || !form.price) {
      toast.error('Nama, Kategori, dan Harga harus diisi');
      return;
    }

    try {
      if (editingMenu) {
        await axios.put(`${API_URL}/admin/menus/${editingMenu.id}`, form, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success('Menu berhasil diupdate');
      } else {
        await axios.post(`${API_URL}/admin/menus`, form, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success('Menu berhasil ditambahkan');
      }
      setShowModal(false);
      fetchMenus();
    } catch (error) {
      toast.error('Gagal menyimpan menu');
      console.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/admin/menus/${menuToDelete.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Menu berhasil dihapus');
      setShowDeleteModal(false);
      fetchMenus();
    } catch (error) {
      toast.error('Gagal menghapus menu');
      console.error(error);
    }
  };

  const handleImportCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        setIsLoading(true);
        let successCount = 0;
        let errorCount = 0;

        for (const row of results.data) {
          const category = (row['Kategori'] || '').trim();
          const name = (row['Nama Menu'] || '').trim();
          const hargaStr = (row['Harga'] || '').trim();
          const description = (row['Deskripsi'] || '').trim();
          const link = (row['Link'] || '').trim();

          if (!name) continue;

          // Format price
          let price = hargaStr;
          if (!isNaN(hargaStr) && hargaStr !== '') {
            price = `Rp ${Number(hargaStr).toLocaleString('id-ID')}`;
          }

          // Active if link exists
          const is_active = link !== '';

          const newMenu = {
            name,
            category: category || (categories.length > 0 ? categories[0].name : ''),
            price,
            description,
            image: link,
            isRecommended: false,
            is_active
          };

          try {
            await axios.post(`${API_URL}/admin/menus`, newMenu, {
              headers: { Authorization: `Bearer ${getToken()}` }
            });
            successCount++;
          } catch (err) {
            console.error('Import error for', name, err);
            errorCount++;
          }
        }

        setIsLoading(false);
        toast.success(`Import selesai: ${successCount} berhasil, ${errorCount} gagal.`);
        fetchMenus();
        event.target.value = null; // Reset input
      },
      error: (error) => {
        toast.error('Gagal membaca file CSV: ' + error.message);
      }
    });
  };

  const handleSaveCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await axios.post(`${API_URL}/admin/menus/categories`, { name: newCategoryName, is_active: true }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setNewCategoryName('');
      fetchCategories();
      toast.success('Kategori ditambahkan');
    } catch (error) {
      toast.error('Gagal menambahkan kategori');
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await axios.delete(`${API_URL}/admin/menus/categories/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      fetchCategories();
      toast.success('Kategori dihapus');
    } catch (error) {
      toast.error('Gagal menghapus kategori');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manajemen Menu</h1>
          <p className="text-gray-500">Kelola daftar menu yang ditampilkan di gallery restaurant.</p>
        </div>
        <div className="flex gap-3">
          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            className="hidden"
            onChange={handleImportCSV}
          />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </Button>
          <Button variant="outline" onClick={() => setShowCategoryModal(true)} className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
            <List className="w-4 h-4 mr-2" />
            Kelola Kategori
          </Button>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Tambah Menu
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gambar</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nama Menu</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kategori</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Harga</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                    Memuat data...
                  </td>
                </tr>
              ) : menus.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                    Belum ada data menu.
                  </td>
                </tr>
              ) : (
                menus.map((menu) => (
                  <tr key={menu.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {menu.image ? (
                        <img src={menu.image} alt={menu.name} className="h-12 w-12 rounded object-cover" />
                      ) : (
                        <div className="h-12 w-12 rounded bg-gray-100 flex items-center justify-center">
                          <ImageIcon className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{menu.name}</div>
                      {menu.isRecommended && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                          Recommended
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {menu.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {menu.price}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        menu.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {menu.is_active ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEdit(menu)}>
                          <Edit2 className="w-4 h-4 text-emerald-600" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => confirmDelete(menu)}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Create/Edit */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMenu ? 'Edit Menu' : 'Tambah Menu Baru'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nama Menu *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Misal: Nasi Goreng Kenari"
                />
              </div>
              <div className="space-y-2">
                <Label>Kategori *</Label>
                <Select
                  value={form.category}
                  onValueChange={(value) => setForm({ ...form, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih Kategori" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id || cat.name} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Harga *</Label>
                <Input
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="Misal: Rp 45.000"
                />
              </div>
              <div className="space-y-2">
                <Label>URL Slug (Opsional)</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="nasi-goreng-kenari (dikosongkan = auto)"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Deskripsi</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Deskripsi lezatnya menu ini..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Gambar Menu</Label>
              {form.image && (
                <div className="relative w-40 h-40 mb-2 rounded-lg overflow-hidden">
                  <img src={form.image} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    onClick={() => setForm({ ...form, image: '' })}
                    className="absolute top-1 right-1 bg-white/80 p-1 rounded-full hover:bg-white text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <Button
                variant="outline"
                onClick={() => setShowImageUpload(true)}
                className="w-full"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                {form.image ? 'Ganti Gambar' : 'Pilih Gambar'}
              </Button>
            </div>

            <div className="flex gap-6 mt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isRecommended"
                  checked={form.isRecommended}
                  onCheckedChange={(checked) => setForm({ ...form, isRecommended: checked })}
                />
                <Label htmlFor="isRecommended">Menu Rekomendasi</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isActive"
                  checked={form.is_active}
                  onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                />
                <Label htmlFor="isActive">Menu Aktif (Ditampilkan)</Label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 border-t pt-4">
            <Button variant="outline" onClick={() => setShowModal(false)}>Batal</Button>
            <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700">
              <Save className="w-4 h-4 mr-2" />
              Simpan
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Delete */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Konfirmasi Hapus</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            Apakah Anda yakin ingin menghapus menu <strong>{menuToDelete?.name}</strong>? Tindakan ini tidak dapat dibatalkan.
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Batal</Button>
            <Button variant="destructive" onClick={handleDelete}>Ya, Hapus</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Upload Dialog */}
      <Dialog open={showImageUpload} onOpenChange={setShowImageUpload}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Pilih Gambar Menu</DialogTitle>
          </DialogHeader>
          <div className="h-[600px] py-4">
            <MediaUpload
              onUploadSuccess={(media) => {
                const url = typeof media === 'string' ? media : media.secure_url || media.url;
                setForm({ ...form, image: url });
                setShowImageUpload(false);
              }}
              onCloseDialog={() => setShowImageUpload(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
      {/* Category Management Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kelola Kategori Menu</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Nama Kategori Baru"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveCategory()}
              />
              <Button onClick={handleSaveCategory} className="bg-emerald-600 hover:bg-emerald-700">Tambah</Button>
            </div>
            <div className="space-y-2 mt-4 max-h-64 overflow-y-auto pr-2">
              {categories.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">Belum ada kategori.</p>
              ) : (
                categories.map((cat) => (
                  <div key={cat.id} className="flex justify-between items-center p-2 bg-gray-50 rounded border">
                    <span className="font-medium">{cat.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteCategory(cat.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-auto">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowCategoryModal(false)}>Tutup</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MenuManagement;
